import { Box, Divider, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { Fragment, useMemo } from "react";
import ShowMoreText from "react-show-more-text";
import { AccountInformation } from "../../../../api-types/account-information";
import { ProfileInformation } from "../../../../api-types/profile-information";
import { BLUE, YELLOW } from "../../../../themes/components/utils";
import { CustomTooltip } from "../../components";
import { SkillsOutput } from "../../components/SkillsOutput";

type ProfileSummaryDescriptionProps = {
  accountInformation?: AccountInformation;
  profileInformation?: ProfileInformation;
  isHovering?: boolean;
  onClick: () => void;
};

const useStyles = makeStyles(() => ({
  root: {
    textDecoration: "none",
    color: YELLOW,
  },
  divider: {
    borderColor: BLUE,
  },
}));

export const ProfileSummaryDescription: React.FC<
  ProfileSummaryDescriptionProps
> = ({ profileInformation, accountInformation, isHovering, onClick }) => {
  const anchorStyles = useStyles();

  const availability = useMemo(() => {
    if (profileInformation?.pricing?.availabilityPerWeek?.type === "fixed") {
      if (profileInformation?.pricing?.availabilityPerWeek?.fixed) {
        return `${profileInformation?.pricing?.availabilityPerWeek.fixed} hrs/week`;
      }
    }

    if (
      profileInformation?.pricing?.availabilityPerWeek?.estimate?.min &&
      profileInformation?.pricing?.availabilityPerWeek?.estimate?.max
    ) {
      return `${profileInformation?.pricing.availabilityPerWeek.estimate.min} - ${profileInformation?.pricing.availabilityPerWeek.estimate.max} hrs/week`;
    }

    return "N/A";
  }, [
    profileInformation?.pricing?.availabilityPerWeek?.estimate?.max,
    profileInformation?.pricing?.availabilityPerWeek?.estimate?.min,
    profileInformation?.pricing?.availabilityPerWeek?.fixed,
    profileInformation?.pricing?.availabilityPerWeek?.type,
  ]);

  const tooltipTitle = useMemo(() => {
    return (
      <Fragment>
        <Box display="flex" flexDirection="column" rowGap={3.5}>
          <Typography
            color="secondary"
            style={{
              fontWeight: 700,
            }}
            variant="subtitle2"
          >
            {accountInformation?.firstName} {accountInformation?.lastName}
          </Typography>
          <Divider className={anchorStyles.divider} />

          <Box display="flex" flexDirection="column" rowGap={7}>
            <Typography
              color="secondary"
              style={{
                fontWeight: 700,
              }}
              variant="subtitle2"
            >
              {profileInformation?.position}
            </Typography>
            <Typography
              color="secondary"
              variant="body1"
              style={{
                whiteSpace: "pre-line",
              }}
            >
              {profileInformation?.description}
            </Typography>
          </Box>
        </Box>
      </Fragment>
    );
  }, [
    accountInformation?.firstName,
    accountInformation?.lastName,
    anchorStyles.divider,
    profileInformation?.description,
    profileInformation?.position,
  ]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      height="100%"
      style={{
        cursor: "pointer",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: "bold",
        }}
      >
        {profileInformation?.position || "No position"}
      </Typography>
      <Box
        onClick={onClick}
        height={200}
        maxWidth={510}
        style={{
          overflow: "auto",
          color: "white",
        }}
      >
        <Typography
          style={{
            fontSize: 16,
          }}
        >
          <ShowMoreText
            lines={3}
            more={
              <CustomTooltip arrow placement="right" title={tooltipTitle}>
                <Typography component="span" variant="body2" color="primary">
                  More
                </Typography>
              </CustomTooltip>
            }
            anchorClass={anchorStyles.root}
            expanded={false}
            // @ts-ignore: A bug in the library. The prop exists, but the interface is not updated.
            expandByClick={false}
          >
            {profileInformation?.description || "No description"}
          </ShowMoreText>
        </Typography>
      </Box>
      <SkillsOutput
        skills={profileInformation?.skills?.map((skill) => skill.name) ?? []}
        color="transparent"
        isHovering={isHovering}
      />
      <Box display="flex" columnGap={5} pt={4}>
        <Box>
          <Typography
            component="span"
            variant="body2"
            style={{
              fontWeight: 500,
            }}
          >
            Fee{" "}
          </Typography>
          <Typography component="span" variant="body2">
            {profileInformation?.pricing?.hourlyRate
              ? `$${profileInformation?.pricing?.hourlyRate}/hr`
              : "N/A"}
          </Typography>
        </Box>
        <Box>
          <Typography
            component="span"
            variant="body2"
            style={{
              fontWeight: 500,
            }}
          >
            Availability{" "}
          </Typography>
          <Typography component="span" variant="body2">
            {availability}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
